
import Vue from 'vue'
import { Prop, Component } from 'vue-property-decorator'
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { downloadFile } from '@/domains/global/functions/util'
import JobActivityActionButton from './JobActivityActionButton.vue'
import ActivityFilesModal from './ActivityFilesModal.vue'
import { TranslationActivity } from '@/domains/global/models'
import ConfirmModal from '@/domains/global/components/ConfirmModal.vue'

@Component({
  components: {
    ActivityFilesModal,
    JobActivityActionButton
  }
})
export default class JobActivityActions extends Vue {
  @Prop({ type: Object as () => TranslationActivity, required: true })
  readonly activity!: TranslationActivity

  @Prop({ type: Boolean, default: false })
  readonly documentsRemoved!: boolean

  @Prop({ type: String, default: 'draft' })
  readonly jobStatus!: string

  @Prop({ type: Boolean, default: false })
  readonly spaced!: boolean

  showFilesModal = false

  get removeModal() {
    return this.$refs.removeMoval as ConfirmModal<TranslationActivity>
  }

  get isPurged() {
    return !!this.activity.isDocumentPurged
  }

  get isCustomerReview() {
    return this.jobStatus === 'requires customer review'
  }

  get isJobCompleted() {
    return this.jobStatus === 'completed'
  }

  get isReviewCompleted() {
    return this.jobStatus === 'review completed'
  }

  get customerActionRequired() {
    return this.jobStatus === 'customer action required'
  }

  get documentType() {
    switch (this.activity.documentType) {
      case 'customer-template':
        return 'Self-Service'
      case 'provider-template':
        return 'Standard'
      default:
        return null
    }
  }

  get viewButtonText() {
    if (this.isCustomerReview) {
      return 'Review'
    } else {
      return 'View'
    }
  }

  get canReviewDocument() {
    return this.jobStatus !== 'draft' && !this.customerActionRequired && (this.activity.documentType === 'customer-template' || this.isCustomerReview)
  }

  get canDownloadDocument() {
    return this.jobStatus !== 'draft' && this.isJobCompleted && !this.isPurged
  }

  get canEdit() {
    return (this.jobStatus === 'draft' && this.activity.isDraft) || (this.customerActionRequired && this.activity.documentType !== 'adhoc')
  }

  get editIcon() {
    if (this.jobStatus === 'draft' && this.activity.isDraft) {
      return 'launch'
    } else {
      return 'edit'
    }
  }

  canDraft(activity: TranslationActivity) {
    return this.jobStatus === 'draft' && !activity.isDraft && activity.documentType === 'customer-template'
  }

  reupload() {
    this.$router.push(`/jobs/${this.$route.params.id}/activities/${this.activity.id}/translate?step=upload`)
  }

  showRemoveModal() {
    this.removeModal.open(this.activity)
  }

  async confirmRemoval() {
    try {
      await axios.delete(`/jobs/${this.activity.jobId}/activities/${this.activity.id}`)
      await this.$store.dispatch('success', 'Translation removed successfully')
      this.$emit('reload')
    } catch (err) {
      Sentry.captureException(err)
      await this.$store.dispatch('error', 'An error occurred removing the translation')
    }
  }

  downloadTranslation() {
    if (this.activity.files) {
      if (this.activity.files.length > 1) {
        this.showFilesModal = true
      } else {
        this.downloadActivityFile(this.activity.files[0].id)
      }
    }
  }

  downloadActivityFile(fileId: string) {
    downloadFile(`${axios.defaults.baseURL}/jobs/${this.activity.jobId}/activities/${this.activity.id}/translations/${fileId}/download`)
  }

  async revertToDraft() {
    try {
      await axios.put(`/jobs/${this.activity.jobId}/activities/${this.activity.id}/draft`)
      await this.$store.dispatch('success', 'Activity reverted to draft')
      this.$emit('reload')
    } catch (error) {
      if (axios.isAxiosError(error) && error.response && error.response.data) {
        const data = error.response.data as { message: string }
        await this.$store.dispatch('error', data.message)
      } else {
        Sentry.captureException(error)
        await this.$store.dispatch('error', 'An error occurred reverting your activity to draft')
      }
    }
  }

  reviewTranslation() {
    if (this.activity.documentType === 'adhoc') {
      this.downloadTranslation()
    } else {
      this.$router.push({ path: `/jobs/${this.$route.params.id}/activities/${this.activity.id}/review`, append: true })
    }
  }
}
